import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { thingsToDoListings } from "./mockListings";
import "./things-to-do.scss";
import {get, post} from "../../utils/request";
import ContactInfoCard from "../FlightCheckout/Passenger/ContactInfoCard";
import { toast } from "react-toastify";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ApplyPromoCode from "../ui/commons/ApplyPromoCode";
import { CalendarDays, CreditCard, Globe2, ShieldCheck, Sparkles, Tag, Wifi } from "lucide-react";
import ErrorModal from "../ui/commons/ErrorModal";
import { AppError, UnauthorizedError } from "../../utils/customError";
import LoginModal from "../../components/Auth/LoginModal";
import { getTimezone } from "../../utils/context";

const ThingsToDoCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();


  const listing = location?.state?.listing;
  const thingsToDoPreorderResult = location?.state?.thingsToDoPreorderResult;

  const userData = useSelector((state) => state.userData.user);

  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  const [preorderResult, setPreorderResult] = useState(thingsToDoPreorderResult || null);

  const [loading, setLoading] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [orderId, setOrderId] = useState(null);

  const [openLoginModal, setOpenLoginModal] = useState(false);

  const items = useSelector((state) => state.thingsToDoCart.items);
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    country: "",
    questionAnswers: {},
  });

  useEffect(() => {
    setPreorderResult(thingsToDoPreorderResult || null);
  }, [thingsToDoPreorderResult]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    if (!userData) return;
    fetchContactInfo();
  }, [userData]);

  async function fetchContactInfo() {
    setContactLoading(true);
    try {
      const result = await get("/contactInfo/getDefault");
      setContactInfo(result);
    } catch (e) {
      setContactInfo(null);
    } finally {
      setContactLoading(false);
    }
  }

  async function contactInfoOnSubmit(values) {
    try {
      if (!values.id) {
        const result = await post("/contactInfo/create", values);
      } else {
        const result = await post(`/contactInfo/update/${values.id}`, values);
      }

      await fetchContactInfo();
    } catch (err) {
      toast.error("Something Went Wrong. Please try again.");
    }
  }

  const onApply = async (promoCode) => {
    preorderResult.preorderInfo.promoCode = promoCode;
    const newPreorderResult = await post('/thingsToDo/preorder',
      preorderResult.preorderInfo
    );
    if (
      newPreorderResult == null ||
      (!!promoCode && newPreorderResult.promoInfo == null)
    ) {
      toast.error("Something went wrong, pleate try again");
      return;
    }

    if (!!promoCode && !!newPreorderResult.promoInfo.hasError) {
      toast.error(newPreorderResult.promoInfo.errorMessage);
      return;
    }

    setPreorderResult(newPreorderResult);
  };

  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

  const bookingQuestions = useMemo(() => {
    const listingIds = [...new Set(items.map((item) => item.listingId))];
    const questionMap = new Map();

    listingIds.forEach((listingId) => {
      const listing = thingsToDoListings.find((item) => item.id === listingId);
      (listing?.otherBookingQuestions || []).forEach((question) => {
        questionMap.set(question.id, question);
      });
    });

    return Array.from(questionMap.values());
  }, [items]);

  function onErrorConfirmed() {
    setShowError(false);
    navigate(`/things-to-do/${listing._id}`);
  }

  async function onFinalStep() {
    if (!userData) {
      setOpenLoginModal(true);
      return;
    }

    const isSuccess = await submitContactInfo();
    if (!isSuccess) {
      return;
    }

    await handlePayment();
  }

  const handlePayment = async () => {
    if (loading) return;

    if (!preorderResult?.preorderInfo?.preorderId) {
      toast.error("Something Went Wrong. Please try again.");
      navigate(`/things-to-do/${listing._id}`, { replace: true });
      return;
    }

    setLoading(true);
    try {
      const timezone = getTimezone();
      const submitOrderResponse = await post("/thingsToDo/submitOrder", {
        preorderId: preorderResult.preorderInfo.preorderId,
        timezone,
      });

      let orderId = submitOrderResponse?.orderId;
      if (!orderId) {
        toast.error("Something went wrong, please try again.");
        return;
      }
      setOrderId(orderId);

      const paymentResult = await post("/payment/pay", {
        orderId: orderId,
        serviceType: "thingsToDo",
      });

      const payResult = paymentResult?.payResult;
      if (!payResult) {
        toast.error("Something went wrong, please try again.");
        return;
      }

      if (payResult.skipPayment === true) {
        navigate(`/my-bookings/thingsToDo/${orderId}`, { replace: true });
        return;
      }

      const options = {
        key: payResult.appkey,
        order_id: payResult.providerOrderId,
        amount: payResult.amount,
        currency: payResult.currency,
        name: payResult.name,
        description: payResult.description,
        timeout: payResult.timeout,
        handler: async () => {
          await paymentNotify(payResult.paymentId);
          setLoading(false);
          navigate(`/my-bookings/thingsToDo/${orderId}`, { replace: true });
        },
        prefill: {
          name: payResult.fullName,
          email: payResult.email,
          contact: payResult.contact,
        },
        theme: { color: "#3399cc" },
        // callback_url: `/my-bookings/thingsToDo/${orderId}`,
        // redirect: true
        modal: {
          ondismiss: function () {
            console.log("Checkout form closed");
            navigate(`/my-bookings/thingsToDo/${orderId}`, { replace: true });
          },
        },
      };

      if (!window.Razorpay) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error("Payment system failed to load. Please try again.");
          return;
        }
      }

      var rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        toast.error("Payment failed");
        navigate(`/my-bookings/thingsToDo/${orderId}`, { replace: true });
      });

      rzp.open();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setOpenLoginModal(true);
        return;
      }
      if (err instanceof AppError && err.code === 50001) {
        showErrorInfo(err.message);
        return;
      }

      console.error("Payment Error:", err);
      toast.error("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  const paymentNotify = async (paymentId) => {
    try {
      if (!paymentId) return;
      await post(`/payment/${paymentId}/notify`);
    } catch {
      // non-blocking
    }
  };

  function showErrorInfo(errorMessage) {
    setErrorMessage(errorMessage);
    setShowError(true);
  }

  async function submitContactInfo() {
    try {
      let preorderInfo = preorderResult.preorderInfo;
      preorderInfo.contactInfo = contactInfo;

      let result = await post("/thingsToDo/preorder", preorderInfo);
      setPreorderResult(result);
      return result;
    } catch (err) {
      if (err instanceof AppError && err.code === 50001) {
        showErrorInfo(err.message);
        return null;
      }

      toast.error("Something went wrong, pleate try again.");
      return null;
    }
  }

  return (
    <>
      <Navbar />
      <main className="things-page py-4 things-checkout-section">
        <div className="container">
          <h1 className="things-title mb-3">Things To Do Checkout</h1>
          <div className="row g-4">
            <div className="col-lg-7">
              <section className="things-section-card mb-3">
                <div className="flex items-center gap-4 rounded-xl">
                  <img
                    src={preorderResult?.preorderInfo?.productImageUrl}
                    alt={preorderResult?.preorderInfo?.productTitle}
                    className="w-20 h-20 rounded-lg object-cover"
                  />

                  <div className="flex-1">
                    <Link
                      to={listing.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <h3
                        className="font-semibold text-gray-900"
                        style={{ fontSize: "16px" }}
                      >
                        {preorderResult?.preorderInfo?.productTitle}
                      </h3>
                    </Link>

                    <p
                      className="text-gray-500 mt-1"
                      style={{ fontSize: "13px", color: "#555" }}
                    >
                      {preorderResult?.preorderInfo?.packageTitle}
                    </p>
                  </div>
                </div>
              </section>

              {/* <section className="things-section-card">
                <h3 className="things-section-title">
                  Participant Information
                </h3>
              </section> */}
              {/* <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={formState.name}
                    onChange={(event) => setFormState({ ...formState, name: event.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    value={formState.email}
                    onChange={(event) => setFormState({ ...formState, email: event.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Country</label>
                  <input
                    className="form-control"
                    value={formState.country}
                    onChange={(event) => setFormState({ ...formState, country: event.target.value })}
                  />
                </div> */}

              <div className="mt-2">
                {!contactLoading && (
                  <ContactInfoCard
                    contactInfo={contactInfo}
                    onSubmit={contactInfoOnSubmit}
                    editable={!contactInfo}
                  />
                )}
              </div>

              {!!bookingQuestions.length && (
                <div className="mt-3">
                  <h5 className="mb-2">Additional booking questions</h5>
                  {bookingQuestions.map((question) => (
                    <div className="mb-2" key={question.id}>
                      <label className="form-label">
                        {question.question}{" "}
                        {question.required ? "*" : "(Optional)"}
                      </label>
                      {question.answer_type === "dropdown" ? (
                        <select
                          className="form-select"
                          value={formState.questionAnswers[question.id] || ""}
                          onChange={(event) =>
                            setFormState({
                              ...formState,
                              questionAnswers: {
                                ...formState.questionAnswers,
                                [question.id]: event.target.value,
                              },
                            })
                          }
                        >
                          <option value="">Select</option>
                          {(question.options || []).map((option) => (
                            <option value={option} key={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="form-control"
                          value={formState.questionAnswers[question.id] || ""}
                          onChange={(event) =>
                            setFormState({
                              ...formState,
                              questionAnswers: {
                                ...formState.questionAnswers,
                                [question.id]: event.target.value,
                              },
                            })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="col-lg-5">
              {/* <h3 className="things-section-title">Booking Summary</h3>
                {!items.length && (
                  <p className="things-muted-text">Your cart is empty.</p>
                )}
                {items.map((item, index) => (
                  <div
                    key={`${item.skuId}-${index}`}
                    className="things-summary-row"
                  >
                    <div>
                      <strong>{item.listingTitle}</strong>
                      <p className="mb-0 things-muted-text">{item.skuTitle}</p>
                    </div>
                    <span>${item.price}</span>
                  </div>
                ))}
                <hr />
                <div className="things-summary-row fw-bold">
                  <span>Total</span>
                  <span>${totalPrice}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-orange w-100 mt-3"
                  disabled={!items.length}
                >
                  Confirm Booking
                </button> */}

              {/* Promo Code */}
              <ApplyPromoCode
                promoCode={preorderResult?.promoInfo?.promoUsage?.promoCode}
                onApply={onApply}
              />

              {/* Price Details */}
              <div className="total-price-box">
                <h6>Price Details</h6>
                {(preorderResult?.priceInfo?.priceDetails || []).map(
                  (priceDetail) => {
                    return (
                      <div>
                        <div className="price-row">
                          <p>{priceDetail.title}</p>
                          <p>{priceDetail.content}</p>
                        </div>
                        {!!priceDetail.children &&
                          priceDetail.children.map((child) => {
                            return (
                              <div className="sub-price-row">
                                <p>{child.title}</p>
                                <p>{child.content}</p>
                              </div>
                            );
                          })}
                      </div>
                    );
                  }
                )}
                <div className="total-price-row">
                  <h2>{preorderResult.priceInfo.totalPrice.title}</h2>
                  <h2>{preorderResult.priceInfo.totalPrice.content}</h2>
                </div>
              </div>

              <div
                className="checkout-card policy-box"
                style={{ marginBottom: "14px" }}
              >
                <strong>Telly Credits</strong>
                <p>
                  Download Teleport Now to unlock member perks, Telly Credits,
                  referral rewards, and app-only travel offers.
                </p>
              </div>

              <button
                className="final-btn"
                onClick={onFinalStep}
                disabled={loading}
              >
                {loading ? "Processing..." : "Proceed to Payment"}
              </button>

              <div
                className="checkout-security-row"
                style={{ marginTop: "14px" }}
              >
                <Sparkles size={15} />
                <span>Secure Payment powered by RazorPay</span>
              </div>
            </div>
          </div>

          <ErrorModal
            open={showError}
            message={errorMessage}
            buttonText="OK"
            onConfirmed={onErrorConfirmed}
          />

          <LoginModal
            isOpen={openLoginModal}
            onClose={() => setOpenLoginModal(false)}
          />
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ThingsToDoCheckout;
